import React, { useState, useRef, Fragment, useEffect, useCallback } from "react";
import { Dialog, DialogPanel, DialogTitle, Transition, TransitionChild } from "@headlessui/react";
import { XMarkIcon, TrashIcon, PlusIcon, EyeIcon, EyeSlashIcon } from "@heroicons/react/24/outline";
import { Button } from "components/ui";
import { Input, Select, Textarea } from "components/ui/Form";
import { getMasterFormFields } from "services/eventScoreService";
import clsx from "clsx";
import axios from "utils/axios";
import { FieldSelectionModal } from "../../../app/pages/settings/sections/builder/FieldSelectionModal";
import { SubmitFormForLead, getFormById, getFieldTypes } from "services/formBuilderService";
import { toast } from "sonner";
import { mutate } from "swr";
import formValidationSchema from "../LeadActions/QuickLeadValidation";
import { updateLeadDetail } from "../../../services/leadsService";
import { getCompanyIdFromToken, getUserIdFromToken } from "../../../utils/tokenUtils";
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import enLocale from 'react-phone-number-input/locale/en.json';

// Field name mapping helper
const getSchemaKey = (fieldName) => {
    const lowerKey = fieldName.toLowerCase();
    if (lowerKey.includes('firstname') || lowerKey.includes('first_name') || lowerKey === 'firstname') {
        return 'firstName';
    } else if (lowerKey.includes('lastname') || lowerKey.includes('last_name') || lowerKey === 'lastname') {
        return 'lastName';
    } else if (lowerKey === 'email') {
        return 'email';
    } else if (lowerKey.includes('phone') || lowerKey.includes('mobile')) {
        return 'phone';
    } else if (lowerKey === 'country') {
        return 'country';
    }
    return null;
};


// Move FieldContainer OUTSIDE the component
const FieldContainer = ({
    children,
    isCheckbox = false,
    fieldId,
    field,
    isHovered,
    isDisabled: isFieldDisabled,
    onMouseEnter,
    onMouseLeave,
    onDelete,
    fieldRefs,
    error
}) => (
    <div
        key={fieldId}
        ref={(el) => el && (fieldRefs[field.FieldName] = el)}
        className={clsx(
            "relative transition-all duration-300 ease-out p-3 rounded-xl border",
            error
                ? "bg-red-50/40 dark:bg-red-900/20 border-red-300 dark:border-red-600"
                : isHovered
                    ? "bg-primary-50/40 dark:bg-primary-900/20 border-primary-300 dark:border-primary-600 transform scale-[1.01] shadow-md"
                    : "bg-white dark:bg-dark-700 border-gray-200 dark:border-dark-600 shadow-sm",
            "hover:shadow-md hover:border-primary-200 dark:hover:border-primary-600"
        )}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
    >
        {/* Floating Tooltip for Field Category (Mandatory / Custom) */}
        {isHovered && (
            <div className="absolute -top-6 left-2 z-30 pointer-events-none">
                <div
                    className={clsx(
                        "px-2 py-1 rounded text-xs font-medium shadow-lg",
                        field.IsMendatory
                            ? "bg-primary-600 text-white"
                            : "bg-primary-600 text-white"
                    )}
                >
                    {field.IsMendatory ? "Mandatory Field" : "Custom Field"}
                </div>
            </div>
        )}

        {/* Delete Button for Custom Fields */}
        {isHovered && field.isCustom && (
            <div className="absolute -top-2 -right-2 z-30">
                <button
                    onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        onDelete(field.Id);
                    }}
                    className="w-8 h-8 bg-white hover:bg-red-50 text-red-600 hover:text-red-700 
         shadow-md hover:shadow-lg transition-all duration-200 rounded-lg 
         border border-red-200 flex items-center justify-center"
                    title="Delete field"
                >
                    <TrashIcon className="w-4 h-4 stroke-2" />
                </button>
            </div>
        )}
        <div className="relative z-10">
            {!isCheckbox && (
                <label
                    htmlFor={fieldId}
                    className={clsx(
                        "block text-sm font-medium mb-1 transition-colors duration-200",
                        error
                            ? "text-red-600 dark:text-red-400"
                            : isHovered
                                ? "text-primary-700 dark:text-primary-300"
                                : "text-gray-700 dark:text-gray-300"
                    )}
                >
                    {field.FieldLabel}
                    {field.IsMendatory ? (
                        <span className="text-red-500 ml-1">*</span>
                    ) : (
                        <span className="text-gray-400 text-xs ml-2 font-normal">(Optional)</span>
                    )}
                </label>
            )}
            {children}
            {error && (
                <p className="text-xs mt-1.5 text-red-600 dark:text-red-400 flex items-center gap-1 animate-fadeIn">
                    <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    {error}
                </p>
            )}
            {!error && field.Description && (
                <p
                    className={clsx(
                        "text-xs mt-1 transition-colors duration-200",
                        isHovered
                            ? "text-primary-600 dark:text-primary-400"
                            : "text-gray-500 dark:text-gray-400"
                    )}
                >
                    {field.Description}
                </p>
            )}
        </div>
    </div>
);

const QuickLeadModal = ({ isOpen, onClose, leadId, categories, onLeadUpdated, selectedForm }) => {
    const cancelButtonRef = useRef(null);
    const hasPatchedRef = useRef(false);
    const [isSaving, setIsSaving] = useState(false);
    const [formFields, setFormFields] = useState([]);
    const [loadingFields, setLoadingFields] = useState(false);
    const [formValues, setFormValues] = useState({});
    const [formErrors, setFormErrors] = useState({});
    const [userEditedFields, setUserEditedFields] = useState(new Set());
    const [hoveredFieldId, setHoveredFieldId] = useState(null);
    const [fieldRefs] = useState({});
    const [isAddFieldModalOpen, setIsAddFieldModalOpen] = useState(false);
    const [visibleFormFields, setVisibleFormFields] = useState([]);
    const [customFields, setCustomFields] = useState([]);
    const [mandatoryFieldsByCategory, setMandatoryFieldsByCategory] = useState([]);
    const [disabledFields, setDisabledFields] = useState(new Set());
    const [currentStep, setCurrentStep] = useState(0);
    const [isMultiStep, setIsMultiStep] = useState(false);
    const [totalSteps, setTotalSteps] = useState(0);
    const [passwordVisible, setPasswordVisible] = useState({}); // Track visibility for password fields

    // Master fields state for FieldSelectionModal (all fields from backend)
    const [allMasterFields, setAllMasterFields] = useState([]);
    const [masterFieldsLoading, setMasterFieldsLoading] = useState(false);
    const [masterFieldsError, setMasterFieldsError] = useState(null);
    const [fieldTypes, setFieldTypes] = useState([]); // Field types for CreateMasterFieldModal

    const isEditMode = !!(leadId && categories && categories.length > 0);

    // Add this useEffect to set disabled fields when in edit mode
    useEffect(() => {
        if (isEditMode && categories && categories.length > 0) {
            const fieldsToDisable = new Set();
            categories.forEach(cat => {
                cat.Fields?.forEach(field => {
                    const fieldName = field.FieldName?.toLowerCase();
                    // Add email and phone fields to disabled set
                    if (fieldName === 'email' ||
                        fieldName === 'phone' ||
                        fieldName === 'phonenumber' ||
                        fieldName === 'mobile' ||
                        fieldName === 'contactnumber' ||
                        fieldName?.includes('phone') ||
                        fieldName?.includes('mobile')) {
                        fieldsToDisable.add(field.FieldName);
                    }
                });
            });
            setDisabledFields(fieldsToDisable);
        }
    }, [isEditMode, categories]);

    // Add this helper function to find a field in master fields
    const findFieldInMasterFields = (fieldName, masterFieldId) => {
        for (const category of formFields) {
            const field = category.MasterFields?.find(
                f => f.FieldName?.toLowerCase() === fieldName?.toLowerCase() || f.Id === masterFieldId
            );
            if (field) {
                return {
                    field: field,
                    category: category
                };
            }
        }
        return null;
    };
    // Updated patchIncomingValues function
    const patchIncomingValues = (categories) => {
        if (!categories || categories.length === 0 || formFields.length === 0) {
            return;
        }

        const patchedValues = {};
        const customFieldsToAdd = [];

        categories.forEach(cat => {
            cat.Fields?.forEach(f => {
                // Only process if FieldValue exists
                if (f.FieldName && f.FieldValue !== undefined && f.FieldValue !== null) {
                    // Check if this field is already in mandatory fields
                    const fieldInfo = findFieldInMasterFields(f.FieldName, f.MasterFieldId);

                    let processedValue = f.FieldValue;
                    if (fieldInfo && fieldInfo.field.HtmlInputType === 'checkbox') {
                        processedValue = Array.isArray(f.FieldValue) ? f.FieldValue : (f.FieldValue ? f.FieldValue.split(',').map(s => s.trim()) : []);
                    }

                    patchedValues[f.FieldName] = processedValue;

                    if (fieldInfo) {
                        const isMandatory = fieldInfo.field.IsMendatory === true;

                        // If field exists but is NOT mandatory, we need to add it as custom
                        if (!isMandatory) {
                            const fieldToAdd = {
                                ...fieldInfo.field,
                                Id: f.MasterFieldId || fieldInfo.field.Id,
                                FieldName: f.FieldName,
                                FieldValue: processedValue,
                                IsMendatory: fieldInfo.field.IsMendatory, // Keep original mandatory status
                                isCustom: true
                            };

                            customFieldsToAdd.push({
                                categoryId: fieldInfo.category.Id,
                                categoryName: fieldInfo.category.CategoryName,
                                field: fieldToAdd
                            });
                        }
                    }
                }
            });
        });

        // Set form values - only patch fields that user hasn't edited yet
        setFormValues(prev => {
            const newValues = { ...prev };
            Object.keys(patchedValues).forEach(fieldName => {
                // Only patch if the user hasn't edited this field yet
                if (!userEditedFields.has(fieldName)) {
                    newValues[fieldName] = patchedValues[fieldName];
                }
            });
            return newValues;
        });

        // Instead of creating separate custom fields, merge them into formFields
        if (customFieldsToAdd.length > 0) {
            setFormFields(prevFormFields => {
                const updatedFormFields = prevFormFields.map(category => {
                    const customFieldsForThisCategory = customFieldsToAdd.filter(
                        item => item.categoryId === category.Id
                    );

                    if (customFieldsForThisCategory.length > 0) {
                        const existingCustomFields = category.MasterFields?.filter(f => f.isCustom) || [];
                        const newCustomFields = customFieldsForThisCategory.map(item => item.field);

                        // Filter out duplicates
                        const uniqueCustomFields = [...existingCustomFields, ...newCustomFields].filter((field, index, self) =>
                            index === self.findIndex(f => f.FieldName === field.FieldName)
                        );

                        return {
                            ...category,
                            MasterFields: [
                                ...(category.MasterFields?.filter(f => !f.isCustom) || []),
                                ...uniqueCustomFields
                            ]
                        };
                    }

                    return category;
                });

                return updatedFormFields;
            });
        }
    };


    useEffect(() => {
        if (formFields.length > 0) {
            setMandatoryFieldsByCategory(getVisibleFields());
        }
    }, [formFields]);

    const fetchFormFields = useCallback(async () => {
        setLoadingFields(true);
        try {
            if (selectedForm && selectedForm.FormId) {
                // Fetch specific form fields
                const result = await getFormById(selectedForm.FormId);
                if (result.success && result.data) {
                    const formData = result.data;
                    let steps = [];

                    // Parse steps if needed or use directly
                    if (formData.Steps) {
                        steps = formData.Steps;
                    }

                    // Map steps to categories
                    const mappedSteps = steps.map(step => {
                        const stepFields = [];
                        if (step.Fields) {
                            step.Fields.forEach(field => {
                                stepFields.push({
                                    Id: field.FieldId || field.Id,
                                    FieldName: field.FieldName,
                                    FieldLabel: field.FieldLabel,
                                    IsMendatory: field.IsRequired,
                                    HtmlInputType: getHtmlInputType(field.FieldTypeId),
                                    MasterFormFieldOptions: field.Options ? field.Options.map(opt => ({
                                        OptionLabel: opt.OptionLabel,
                                        OptionValue: opt.OptionValue
                                    })) : [],
                                    FieldOrder: field.FieldOrder,
                                    Description: field.Placeholder,
                                    MasterFieldId: field.MasterFieldId
                                });
                            });
                            // Sort fields
                            stepFields.sort((a, b) => (a.FieldOrder || 0) - (b.FieldOrder || 0));
                        }
                        return {
                            Id: step.Id || `step-${Math.random()}`,
                            CategoryName: step.StepTitle || formData.Title || "Lead Details",
                            MasterFields: stepFields
                        };
                    });

                    setFormFields(mappedSteps);
                    initializeFormValues(mappedSteps);

                    if (mappedSteps.length > 1) {
                        setIsMultiStep(true);
                        setTotalSteps(mappedSteps.length);
                        setCurrentStep(0);
                    } else {
                        setIsMultiStep(false);
                    }
                }
            } else {
                // Default behavior: Fetch Master Fields
                const result = await getMasterFormFields();
                if (result.success) {
                    // Sort fields by FieldOrder within each category
                    const sortedData = result.data.map(category => ({
                        ...category,
                        MasterFields: category.MasterFields?.sort((a, b) => {
                            // Handle null FieldOrder - push to end
                            if (a.FieldOrder === null && b.FieldOrder === null) return 0;
                            if (a.FieldOrder === null) return 1;
                            if (b.FieldOrder === null) return -1;
                            // Sort by FieldOrder ascending
                            return a.FieldOrder - b.FieldOrder;
                        })
                    }));

                    setFormFields(sortedData);
                    setIsMultiStep(false);

                    if (!isEditMode) {
                        initializeFormValues(sortedData);
                    }
                } else {
                    console.error("Failed to load form fields", result.message);
                }
            }
        } catch (error) {
            console.error("Error fetching form fields", error);
        } finally {
            setLoadingFields(false);
        }
    }, [isEditMode, selectedForm]);

    // Helper to map FieldTypeId to HTML Input Type (Corrected mapping)
    const getHtmlInputType = (typeId) => {
        switch (typeId) {
            case 1: return "text";
            case 2: return "textarea";
            case 3: return "number";
            case 4: return "date";
            case 5: return "checkbox";
            case 6: return "radio";
            case 7: return "select";
            case 8: return "email";
            case 9: return "password";
            case 10: return "phone";
            default: return "text";
        }
    };


    // Update the useEffect to call patchIncomingValues after formFields is set
    useEffect(() => {
        if (isOpen) {
            // Reset user edited fields when modal opens
            setUserEditedFields(new Set());
            fetchFormFields();
        }
    }, [isOpen, fetchFormFields]);

    // Add a separate useEffect to patch values when formFields are loaded
    // Use a ref to track if we've already patched to avoid infinite loops
    useEffect(() => {
        if (formFields.length > 0 && categories && categories.length > 0 && isEditMode && !hasPatchedRef.current) {
            patchIncomingValues(categories);
            hasPatchedRef.current = true;
        }
    }, [formFields]); // Only depend on formFields

    // Reset the patch flag when modal closes
    useEffect(() => {
        if (!isOpen) {
            hasPatchedRef.current = false;
        }
    }, [isOpen]);
    const initializeFormValues = (data) => {
        const initialValues = {};
        data.forEach((category) => {
            category.MasterFields?.forEach((field) => {
                const fieldNameLower = (field.FieldName || "").toLowerCase();

                // Pre-set Country to India if found in options
                if (fieldNameLower === 'country' || fieldNameLower === 'countryid') {
                    const options = field.MasterFormFieldOptions || [];
                    const indiaOption = options.find(opt => {
                        const label = (opt.OptionLabel || opt.optionLabel || "").toLowerCase();
                        const val = (opt.OptionValue || opt.optionValue || "").toLowerCase();
                        return label === "india" || val === "in" || val === "india";
                    });

                    if (indiaOption) {
                        initialValues[field.FieldName] = indiaOption.OptionValue || indiaOption.optionValue;
                    } else {
                        // If no specific option found, we can try to set a fallback "IN" if it's likely a code-based list
                        // but usually it's better to stick to what the user requested: preselect India.
                        initialValues[field.FieldName] = "India"; // Default string fallback
                    }
                } else if (field.IsMendatory) {
                    initialValues[field.FieldName] = field.DefaultValue || "";
                }
            });
        });
        setFormValues(initialValues);
    };

    const getVisibleFields = () => {
        const visibleFields = [];
        const isStandardLead = !selectedForm || !selectedForm.FormId;

        formFields.forEach((category) => {
            const categoryFields = category.MasterFields || [];

            // Filter fields
            const filteredFields = categoryFields.filter(field => {
                // If it's a "Standard Lead", ONLY show mandatory fields OR custom fields (which are manually added)
                if (isStandardLead) {
                    return field.IsMendatory || field.isCustom;
                }
                return true;
            });

            if (filteredFields.length > 0) {
                visibleFields.push({
                    categoryName: category.CategoryName,
                    categoryId: category.Id,
                    fields: filteredFields,
                    isCustom: false
                });
            }
        });
        return visibleFields;
    };

    // Improved validate single field function
    const validateField = async (fieldName, value) => {
        try {
            const schemaKey = getSchemaKey(fieldName);

            if (schemaKey && formValidationSchema.fields[schemaKey]) {
                // Validate the field
                await formValidationSchema.fields[schemaKey].validate(value);

                // Clear error if validation passes
                setFormErrors((prev) => {
                    const newErrors = { ...prev };
                    delete newErrors[fieldName];
                    return newErrors;
                });
            }
        } catch (error) {
            // Set error if validation fails
            setFormErrors((prev) => ({
                ...prev,
                [fieldName]: error.message
            }));
        }
    };

    const handleFieldChange = (fieldName, value) => {
        setFormValues((prev) => ({
            ...prev,
            [fieldName]: value,
        }));

        // Track that this field has been edited by the user
        setUserEditedFields(prev => new Set([...prev, fieldName]));

        // Debounced real-time validation
        validateField(fieldName, value);
    };

    const handleDeleteField = (fieldId) => {
        let updatedFormFields = [...formFields];
        let fieldFound = false;

        // Remove from main formFields
        updatedFormFields = updatedFormFields.map((category) => {
            const filteredFields = category.MasterFields?.filter(
                (f) => f.Id !== fieldId
            ) || [];
            if (filteredFields.length !== category.MasterFields?.length) {
                fieldFound = true;
            }
            return {
                ...category,
                MasterFields: filteredFields,
            };
        });

        // Remove from customFields
        let updatedCustomFields = customFields.map((category) => {
            const filteredFields = category.fields?.filter(
                (f) => f.Id !== fieldId
            ) || [];
            return {
                ...category,
                fields: filteredFields,
            };
        }).filter(category => category.fields.length > 0);

        if (!fieldFound) {
            updatedCustomFields = updatedCustomFields.filter(
                (cat) => cat.fields.length > 0
            );
        }

        setFormFields(updatedFormFields);
        setCustomFields(updatedCustomFields);

        // Remove from formValues and errors
        setFormValues((prev) => {
            const newValues = { ...prev };
            Object.keys(newValues).forEach((key) => {
                const allFields = [
                    ...updatedFormFields.flatMap((c) => c.MasterFields || []),
                    ...updatedCustomFields.flatMap((c) => c.fields || []),
                ];
                const stillExists = allFields.some((f) => f.FieldName === key);
                if (!stillExists) delete newValues[key];
            });
            return newValues;
        });

        setFormErrors((prev) => {
            const newErrors = { ...prev };
            const allFields = [
                ...updatedFormFields.flatMap((c) => c.MasterFields || []),
                ...updatedCustomFields.flatMap((c) => c.fields || []),
            ];
            Object.keys(newErrors).forEach((key) => {
                const stillExists = allFields.some((f) => f.FieldName === key);
                if (!stillExists) delete newErrors[key];
            });
            return newErrors;
        });
    };

    const handleSelectCustomField = (field) => {

        const fieldExistsInMandatory = formFields.some(category =>
            category.MasterFields?.some(f => f.FieldName === field.FieldName && f.IsMendatory === true)
        );

        const fieldExistsInCustom = customFields.some(cat =>
            cat.fields?.some(f => f.FieldName === field.FieldName)
        );

        if (fieldExistsInMandatory || fieldExistsInCustom) {
            return;
        }

        const newField = {
            ...field,
            IsMendatory: field.IsMendatory, // Keep original mandatory status
            Id: field.Id || `custom-${Date.now()}`,
            isCustom: true,
        };

        const categoryExistsInMandatory = formFields.some(
            (cat) => cat.Id === field.CategoryId
        );

        if (categoryExistsInMandatory) {
            setFormFields((prev) =>
                prev.map((cat) => {
                    if (cat.Id === field.CategoryId) {
                        return {
                            ...cat,
                            MasterFields: [...(cat.MasterFields || []), newField],
                        };
                    }
                    return cat;
                })
            );
        } else {
            setCustomFields((prev) => {
                const existingCategoryIndex = prev.findIndex(
                    (cat) => cat.categoryId === field.CategoryId
                );

                if (existingCategoryIndex !== -1) {
                    const updated = [...prev];
                    updated[existingCategoryIndex] = {
                        ...updated[existingCategoryIndex],
                        fields: [...updated[existingCategoryIndex].fields, newField],
                    };
                    return updated;
                } else {
                    return [
                        ...prev.filter(cat => cat.categoryId !== field.CategoryId),
                        {
                            categoryName: field.CategoryName,
                            categoryId: field.CategoryId,
                            fields: [newField],
                            isCustom: true,
                        },
                    ];
                }
            });
        }

        setFormValues((prev) => ({
            ...prev,
            [field.FieldName]: field.DefaultValue || "",
        }));

    };

    // Handler for FieldSelectionModal - handles master field selection from form builder modal
    const handleSelectMasterField = (fieldData) => {
        // Convert from FieldSelectionModal format (camelCase) to QuickLeadModal format (PascalCase)
        const field = {
            Id: fieldData.masterFieldId || fieldData.Id || `master-${Date.now()}`,
            FieldName: fieldData.fieldName || fieldData.FieldName,
            FieldLabel: fieldData.fieldLabel || fieldData.FieldLabel,
            HtmlInputType: fieldData.htmlInputType || fieldData.HtmlInputType || 'text',
            IsMendatory: fieldData.isMendatory || fieldData.IsMendatory || false, // Keep original mandatory status
            isCustom: true,
            MasterFieldId: fieldData.masterFieldId || fieldData.Id,
            MasterFormFieldOptions: fieldData.options || fieldData.MasterFormFieldOptions || [],
            DefaultValue: fieldData.defaultValue || fieldData.DefaultValue || '',
            Placeholder: fieldData.placeholder || fieldData.Placeholder || '',
            CategoryId: fieldData.CategoryId,
            CategoryName: fieldData.CategoryName,
        };

        // Get the visible fields (what's actually being rendered in the form)
        const visibleCategories = getVisibleFields();

        const fieldAlreadyVisible = visibleCategories.some(category =>
            category.fields?.some(f =>
                f.FieldName === field.FieldName ||
                (f.Id && f.Id === field.Id) ||
                (f.MasterFieldId && f.MasterFieldId === field.MasterFieldId)
            )
        );

        // Also check in customFields
        const fieldExistsInCustom = customFields.some(cat =>
            cat.fields?.some(f =>
                f.FieldName === field.FieldName
            )
        );

        if (fieldAlreadyVisible || fieldExistsInCustom) {
            toast.info("This field is already added to the form");
            return;
        }

        let addedToExistingCategory = false;

        setFormFields((prev) => {
            const updated = prev.map((cat) => {
                // Match by category ID or category name
                if (cat.Id === field.CategoryId || cat.CategoryName === field.CategoryName) {
                    addedToExistingCategory = true;
                    return {
                        ...cat,
                        MasterFields: [...(cat.MasterFields || []), field],
                    };
                }
                return cat;
            });

            // If no matching category found, add to first category
            if (!addedToExistingCategory && updated.length > 0) {
                updated[0] = {
                    ...updated[0],
                    MasterFields: [...(updated[0].MasterFields || []), field],
                };
            }

            return updated;
        });

        // Initialize form value for this field
        setFormValues((prev) => ({
            ...prev,
            [field.FieldName]: field.DefaultValue || "",
        }));

        toast.success(`"${field.FieldLabel}" field added`);
    };

    // Fetch all master fields from backend for the FieldSelectionModal
    const fetchAllMasterFields = async () => {
        setMasterFieldsLoading(true);
        setMasterFieldsError(null);

        try {
            const response = await axios.get('/api/FormDesk/GetMasterFields');

            if (response.data?.status === "Success" && response.data?.data) {
                const parsedData = JSON.parse(response.data.data);
                setAllMasterFields(parsedData);
            } else {
                setMasterFieldsError(response.data?.message || 'Failed to fetch master fields');
            }
        } catch (error) {
            console.error('Error fetching master fields:', error);
            setMasterFieldsError('Failed to load master fields');
        } finally {
            setMasterFieldsLoading(false);
        }
    };

    // Fetch field types from backend for CreateMasterFieldModal
    const fetchFieldTypes = async () => {
        try {
            const result = await getFieldTypes();
            if (result.success && result.data) {
                setFieldTypes(result.data);
            }
        } catch (error) {
            console.error('Error fetching field types:', error);
        }
    };

    const handleAddField = () => {
        // Fetch all master fields and field types when opening the modal
        fetchAllMasterFields();
        fetchFieldTypes();
        setIsAddFieldModalOpen(true);
    };

    // Render Field - Now with error handling
    const renderField = useCallback((field, fieldIdx, categoryIdx) => {
        const fieldType =
            field.HtmlInputType || field.TypeName?.toLowerCase() || "text";
        const fieldValue = formValues[field.FieldName] || (fieldType === 'checkbox' ? [] : "");
        const fieldError = formErrors[field.FieldName];
        const fieldId = field.Id || `${categoryIdx}-${fieldIdx}`;
        const isHovered = hoveredFieldId === fieldId;
        // Check if this field should be disabled
        const isFieldDisabled = isEditMode && disabledFields.has(field.FieldName);

        const commonProps = {
            id: fieldId,
            name: field.FieldName,
            value: fieldValue,
            placeholder: field.Placeholder || field.FieldLabel,
            required: field.IsMendatory,
            disabled: isFieldDisabled, // Add this line
            onChange: (e) => handleFieldChange(field.FieldName, e.target.value),
            onBlur: (e) => validateField(field.FieldName, e.target.value),
            className: clsx(
                "transition-all duration-200",
                fieldError && "border-red-300 focus:border-red-500 focus:ring-red-500",
                isFieldDisabled && "bg-gray-100 dark:bg-dark-600 cursor-not-allowed opacity-75" // Add styling for disabled fields
            ),
        };

        const containerProps = {
            fieldId,
            field,
            isHovered,
            onMouseEnter: () => setHoveredFieldId(fieldId),
            onMouseLeave: () => setHoveredFieldId(null),
            onDelete: handleDeleteField,
            fieldRefs,
            error: fieldError
        };

        switch (fieldType) {
            case "textarea":
                return (
                    <FieldContainer {...containerProps}>
                        <Textarea {...commonProps} rows={4} />
                    </FieldContainer>
                );

            case "select":
            case "dropdown": {
                const options = field.MasterFormFieldOptions || [];
                return (
                    <FieldContainer {...containerProps}>
                        <Select
                            {...commonProps}
                            onChange={(e) => {
                                // Handle both event objects and direct values, ensure string value
                                const selectedValue = e?.target?.value ?? e;
                                handleFieldChange(field.FieldName, selectedValue);
                            }}
                        >
                            <option value="">Select {field.FieldLabel}</option>
                            {options.map((opt, idx) => (
                                <option key={idx} value={opt.OptionValue || opt.optionValue}>
                                    {opt.OptionLabel || opt.optionLabel}
                                </option>
                            ))}
                        </Select>
                    </FieldContainer>
                );
            }

            case "checkbox":
                {
                    const checkOptions = field.MasterFormFieldOptions || [];
                    // If no options, basic bool checkbox (fallback)
                    if (checkOptions.length === 0) {
                        return (
                            <FieldContainer {...containerProps} isCheckbox>
                                <div className="flex items-center">
                                    <input
                                        type="checkbox"
                                        {...commonProps}
                                        checked={fieldValue === true || fieldValue === "true"}
                                        onChange={(e) =>
                                            handleFieldChange(field.FieldName, e.target.checked)
                                        }
                                        className="h-4 w-4 focus:ring-2 border-gray-300 rounded text-primary-600 focus:ring-primary-500"
                                    />
                                    <label
                                        htmlFor={fieldId}
                                        className="ml-2 block text-sm text-gray-900 dark:text-gray-100"
                                    >
                                        {field.FieldLabel}
                                    </label>
                                </div>
                            </FieldContainer>
                        );
                    }
                    // Multi-option checkbox
                    return (
                        <FieldContainer {...containerProps}>
                            <div className="space-y-2 p-1">
                                {checkOptions.map((option, idx) => {
                                    const optionValue = option.OptionValue || option.optionValue;
                                    const optionLabel = option.OptionLabel || option.optionLabel;
                                    const isChecked = Array.isArray(fieldValue) ? fieldValue.includes(optionValue) : false;

                                    return (
                                        <label key={idx} className="flex items-center cursor-pointer hover:bg-gray-50 dark:hover:bg-dark-600 p-2 rounded-md transition-colors duration-150">
                                            <div className="relative mr-3">
                                                <input
                                                    type="checkbox"
                                                    checked={isChecked}
                                                    onChange={(e) => {
                                                        let newValues = Array.isArray(fieldValue) ? [...fieldValue] : [];
                                                        if (e.target.checked) {
                                                            newValues.push(optionValue);
                                                        } else {
                                                            newValues = newValues.filter(v => v !== optionValue);
                                                        }
                                                        handleFieldChange(field.FieldName, newValues);
                                                    }}
                                                    className="sr-only"
                                                />
                                                <div className={clsx(
                                                    "w-5 h-5 rounded border-2 transition-all duration-200 cursor-pointer flex items-center justify-center",
                                                    isChecked
                                                        ? "border-primary-600 bg-primary-600"
                                                        : "bg-white dark:bg-dark-700 border-gray-300 dark:border-gray-500 hover:border-primary-400"
                                                )}>
                                                    {isChecked && (
                                                        <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                                        </svg>
                                                    )}
                                                </div>
                                            </div>
                                            <span className="text-sm font-medium select-none text-gray-700 dark:text-gray-200">
                                                {optionLabel}
                                            </span>
                                        </label>
                                    );
                                })}
                            </div>
                        </FieldContainer>
                    );
                }

            case "radio": {
                const radioOptions = field.MasterFormFieldOptions || [];
                return (
                    <FieldContainer {...containerProps}>
                        <div className="space-y-2 p-1">
                            {radioOptions.map((option, idx) => {
                                const optionId = `${fieldId}-${idx}`;
                                const optionValue = option.OptionValue || option.optionValue;
                                const optionLabel = option.OptionLabel || option.optionLabel;
                                const isSelected = fieldValue === optionValue;

                                return (
                                    <label key={idx} className="flex items-center cursor-pointer hover:bg-gray-50 dark:hover:bg-dark-600 p-2 rounded-md transition-colors duration-150">
                                        <div className="relative mr-3">
                                            <input
                                                type="radio"
                                                id={optionId}
                                                name={field.FieldName}
                                                value={optionValue}
                                                checked={isSelected}
                                                onChange={(e) =>
                                                    handleFieldChange(field.FieldName, e.target.value)
                                                }
                                                className="sr-only"
                                            />
                                            <div className={clsx(
                                                "w-5 h-5 rounded-full border-2 transition-all duration-200 cursor-pointer flex items-center justify-center",
                                                isSelected
                                                    ? "border-primary-600 bg-primary-600"
                                                    : "bg-white dark:bg-dark-700 border-gray-300 dark:border-gray-500 hover:border-primary-400"
                                            )}>
                                                {isSelected && (
                                                    <div className="w-2 h-2 bg-white rounded-full"></div>
                                                )}
                                            </div>
                                        </div>
                                        <span className="text-sm font-medium select-none text-gray-700 dark:text-gray-200">
                                            {optionLabel}
                                        </span>
                                    </label>
                                );
                            })}
                        </div>
                    </FieldContainer>
                );
            }

            // case "number":
            //     return (
            //         <FieldContainer {...containerProps}>
            //             <Input {...commonProps} type="number" />
            //         </FieldContainer>
            //     );

            case "email":
                return (
                    <FieldContainer {...containerProps}>
                        <Input {...commonProps} type="email" />
                    </FieldContainer>
                );

            case "date":
                return (
                    <FieldContainer {...containerProps}>
                        <Input {...commonProps} type="date" />
                    </FieldContainer>
                );

            case "tel":
            case "phone":
                return (
                    <FieldContainer {...containerProps}>
                        <div className={clsx(
                            "relative rounded-md border transition-all duration-200 bg-white dark:bg-dark-700",
                            fieldError
                                ? "border-red-300 focus-within:border-red-500 focus-within:ring-1 focus-within:ring-red-500"
                                : "border-gray-300 dark:border-dark-500 focus-within:border-primary-500 focus-within:ring-1 focus-within:ring-primary-500"
                        )}>
                            <PhoneInput
                                international
                                defaultCountry="IN"
                                value={fieldValue}
                                onChange={(value) => handleFieldChange(field.FieldName, value)}
                                onCountryChange={(countryCode) => {
                                    if (countryCode && enLocale[countryCode]) {
                                        const countryName = enLocale[countryCode];
                                        let matchedFieldName = null;
                                        let matchedValue = null;

                                        // Lookup the "Country" field in the form schema
                                        // First check standard form fields
                                        for (const cat of formFields) {
                                            const cField = cat.MasterFields?.find(f => {
                                                const lower = f.FieldName?.toLowerCase();
                                                return lower === 'country' || lower === 'countryid';
                                            });
                                            if (cField) {
                                                matchedFieldName = cField.FieldName;
                                                const opts = cField.MasterFormFieldOptions || [];
                                                const opt = opts.find(o => 
                                                    (o.OptionLabel || o.optionLabel || '').toLowerCase() === countryName.toLowerCase()
                                                );
                                                matchedValue = opt ? (opt.OptionValue || opt.optionValue) : countryName;
                                                break;
                                            }
                                        }

                                        // If not found, check custom fields
                                        if (!matchedFieldName) {
                                            for (const cat of customFields) {
                                                const cField = cat.fields?.find(f => {
                                                    const lower = f.FieldName?.toLowerCase();
                                                    return lower === 'country' || lower === 'countryid';
                                                });
                                                if (cField) {
                                                    matchedFieldName = cField.FieldName;
                                                    const opts = cField.MasterFormFieldOptions || [];
                                                    const opt = opts.find(o => 
                                                        (o.OptionLabel || o.optionLabel || '').toLowerCase() === countryName.toLowerCase()
                                                    );
                                                    matchedValue = opt ? (opt.OptionValue || opt.optionValue) : countryName;
                                                    break;
                                                }
                                            }
                                        }

                                        if (matchedFieldName && matchedValue) {
                                            handleFieldChange(matchedFieldName, matchedValue);
                                        }
                                    }
                                }}
                                placeholder={field.Placeholder || "Enter phone number"}
                                disabled={isFieldDisabled}
                                className={clsx(
                                    "p-2 bg-transparent",
                                    isFieldDisabled && "bg-gray-100 dark:bg-dark-600 cursor-not-allowed opacity-75"
                                )}
                                numberInputProps={{
                                    className: "w-full bg-transparent border-none focus:ring-0 p-0 text-sm text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none", // Remove default input styles
                                    required: field.IsMendatory
                                }}
                            />
                        </div>
                    </FieldContainer>
                );

            case "password":
                return (
                    <FieldContainer {...containerProps}>
                        <div className="relative">
                            <Input
                                {...commonProps}
                                type={passwordVisible[fieldId] ? "text" : "password"}
                                className={clsx(
                                    commonProps.className,
                                    "pr-10" // Make room for the toggle button
                                )}
                            />
                            <button
                                type="button"
                                onClick={() => setPasswordVisible(prev => ({
                                    ...prev,
                                    [fieldId]: !prev[fieldId]
                                }))}
                                className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 transition-colors"
                                tabIndex={-1}
                            >
                                {passwordVisible[fieldId] ? (
                                    <EyeSlashIcon className="h-5 w-5" aria-hidden="true" />
                                ) : (
                                    <EyeIcon className="h-5 w-5" aria-hidden="true" />
                                )}
                            </button>
                        </div>
                    </FieldContainer>
                );

            default:
                return (
                    <FieldContainer {...containerProps}>
                        <Input {...commonProps} type="text" />
                    </FieldContainer>
                );
        }
    }, [formValues, hoveredFieldId, fieldRefs, formErrors, isEditMode, disabledFields, passwordVisible, formFields, customFields]);

    const handleClose = () => {
        setFormValues({});
        setFormErrors({});
        setUserEditedFields(new Set());
        setHoveredFieldId(null);
        setDisabledFields(new Set());
        setPasswordVisible({});
        onClose();
    };

    // Comprehensive validation on save
    const handleSave = async () => {
        setIsSaving(true);

        const valuesToValidate = {};
        const fieldNameMapping = {};

        try {
            // Only apply schema validation for Standard Lead mode (no specific form selected)
            const isStandardLead = !selectedForm || !selectedForm.FormId;

            if (isStandardLead) {
                // Map form values to validation schema keys
                Object.keys(formValues).forEach(key => {
                    const schemaKey = getSchemaKey(key);
                    if (schemaKey) {
                        valuesToValidate[schemaKey] = formValues[key];
                        fieldNameMapping[schemaKey] = key;
                    }
                });

                // Validate all fields at once using the schema (only for standard lead)
                await formValidationSchema.validate(valuesToValidate, { abortEarly: false });
            }

            // Check for any remaining mandatory fields (applies to both standard and form-specific modes)
            const visibleCategories = getVisibleFields();
            let hasEmptyMandatory = false;
            const newErrors = {};

            visibleCategories.forEach((category) => {
                category.fields.forEach((field) => {
                    if (
                        field.IsMendatory &&
                        (!formValues[field.FieldName] || formValues[field.FieldName].toString().trim() === "")
                    ) {
                        hasEmptyMandatory = true;
                        newErrors[field.FieldName] = `${field.FieldLabel} is required`;
                    }
                });
            });

            if (hasEmptyMandatory) {
                setFormErrors(newErrors);
                toast.error("Please fill all mandatory fields correctly");

                // Auto-scroll to first error
                const firstErrorField = Object.keys(newErrors)[0];
                if (firstErrorField && fieldRefs[firstErrorField]) {
                    setTimeout(() => {
                        fieldRefs[firstErrorField].scrollIntoView({
                            behavior: 'smooth',
                            block: 'center'
                        });
                    }, 100);
                }

                setIsSaving(false);
                return;
            }

            // Build fieldSubmissions array
            const fieldSubmissions = [];
            const seenFields = new Set(); // Track unique masterFieldId to avoid duplicates

            [...formFields, ...customFields].forEach((category) => {
                const fields = category.MasterFields || category.fields || [];
                fields.forEach((field) => {
                    if (formValues[field.FieldName] !== undefined) {
                        // Use MasterFieldId if available (for form-specific fields), otherwise use Id
                        const masterFieldId = field.MasterFieldId || field.Id;
                        const uniqueKey = masterFieldId;
                        if (!seenFields.has(uniqueKey)) {
                            seenFields.add(uniqueKey);

                            // Get the field type to determine value format
                            const fieldType = field.HtmlInputType || field.TypeName?.toLowerCase() || "text";
                            let fieldValue = formValues[field.FieldName];

                            // Convert arrays to comma-separated strings (for checkboxes and multi-select)
                            // This matches the FormPage.jsx behavior
                            if (Array.isArray(fieldValue)) {
                                fieldValue = fieldValue.join(', ');
                            }

                            fieldSubmissions.push({
                                masterFieldId: masterFieldId,
                                masterFieldName: field.FieldName,
                                value: fieldValue,
                            });
                        }
                    }
                });
            });

            let result;

            if (isEditMode) {
                // Update mode - call updateLeadDetail API
                const payload = {
                    leadId: leadId,
                    companyId: getCompanyIdFromToken(),
                    loggedInUserId: getUserIdFromToken(),
                    fieldSubmissions: fieldSubmissions
                };

                result = await updateLeadDetail(payload);

                if (result.success) {
                    toast.success(result.message || "Lead updated successfully!");

                    // Call the callback to refresh lead data
                    if (onLeadUpdated && typeof onLeadUpdated === 'function') {
                        await onLeadUpdated();
                    }

                    // Trigger SWR revalidation
                    mutate(
                        key => Array.isArray(key) && key[0] === 'leads',
                        undefined,
                        { revalidate: true }
                    );

                    handleClose();
                } else {
                    toast.error(result.message || "Failed to update lead");
                }
            } else {
                // Create mode - call SubmitFormForLead API
                const payload = {
                    fieldSubmissions,
                    LeadSourceFormId: selectedForm?.FormId || null
                };

                result = await SubmitFormForLead(payload);

                if (result.success) {
                    toast.success(result.message || "Lead created successfully!");

                    // Trigger SWR revalidation
                    mutate(
                        key => Array.isArray(key) && key[0] === 'leads',
                        undefined,
                        { revalidate: true }
                    );

                    handleClose();
                } else {
                    toast.error(result.message || "Failed to save lead");
                }
            }
        } catch (error) {
            if (error.name === 'ValidationError') {
                // Handle Yup validation errors
                const newErrors = {};
                error.inner.forEach((err) => {
                    // Map back to original field names
                    const originalFieldName = fieldNameMapping[err.path] || err.path;
                    newErrors[originalFieldName] = err.message;
                });
                setFormErrors(newErrors);
                toast.error("Please fix the validation errors before submitting");

                // Auto-scroll to first error
                const firstErrorField = Object.keys(newErrors)[0];
                if (firstErrorField && fieldRefs[firstErrorField]) {
                    setTimeout(() => {
                        fieldRefs[firstErrorField].scrollIntoView({
                            behavior: 'smooth',
                            block: 'center'
                        });
                    }, 100);
                }
            } else {
                console.error("Error saving lead:", error);
                toast.error("An unexpected error occurred while saving the lead");
            }
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <>
            <FieldSelectionModal
                isOpen={isAddFieldModalOpen}
                onClose={() => setIsAddFieldModalOpen(false)}
                masterFields={allMasterFields}
                masterFieldsLoading={masterFieldsLoading}
                masterFieldsError={masterFieldsError}
                onSelectField={handleSelectMasterField}
                onRefreshMasterFields={fetchAllMasterFields}
                fieldTypes={fieldTypes}
            />

            <Transition appear show={isOpen} as={Fragment}>
                <Dialog
                    as="div"
                    className="fixed inset-0 z-[100] flex items-center justify-center overflow-hidden px-4 py-6 sm:px-5"
                    onClose={() => { }}
                    initialFocus={cancelButtonRef}
                >
                    <TransitionChild
                        as={Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <div className="absolute inset-0 bg-gray-900/50 backdrop-blur dark:bg-black/30" />
                    </TransitionChild>

                    <TransitionChild
                        as={Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0 scale-95"
                        enterTo="opacity-100 scale-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100 scale-100"
                        leaveTo="opacity-0 scale-95"
                    >
                        <DialogPanel className="relative flex w-full max-w-2xl origin-top flex-col overflow-hidden rounded-xl bg-white shadow-2xl transition-all duration-300 dark:bg-dark-700 outline-none focus:outline-none">
                            {/* HEADER */}
                            <div className="border-b border-gray-200 dark:border-dark-600 px-6 py-4 flex items-center justify-between bg-gray-50 dark:bg-dark-700">
                                <div>
                                    <h2 className="text-lg font-semibold text-primary-600 dark:text-primary-400">
                                        {isEditMode ? "Edit Lead" : "Quick Lead"}
                                    </h2>
                                    <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                                        {isEditMode
                                            ? "Update lead information by editing the fields below"
                                            : "Add a new lead quickly by filling mandatory fields"}
                                    </p>
                                </div>

                                <div className="flex items-center gap-3">
                                    <div
                                        className="flex items-center gap-1.5 bg-white dark:bg-dark-750 rounded-lg px-3 py-1.5 border border-gray-200 dark:border-dark-600 shadow-sm cursor-pointer hover:bg-gray-50 dark:hover:bg-dark-700 transition-colors duration-200"
                                        onClick={handleAddField}
                                    >
                                        <PlusIcon className="w-3.5 h-3.5 text-primary-600 dark:text-primary-400" />
                                        <span className="text-xs font-medium text-primary-600 dark:text-primary-400">Add Field</span>
                                    </div>

                                    <div className="h-6 w-px bg-gray-300 dark:bg-dark-600"></div>

                                    <Button
                                        onClick={handleClose}
                                        variant="flat"
                                        isIcon
                                        className="size-8 rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-dark-600 dark:hover:text-gray-300 transition-all duration-200"
                                    >
                                        <XMarkIcon className="size-5" />
                                    </Button>
                                </div>
                            </div>

                            <div className="flex flex-col overflow-y-auto px-6 py-5 max-h-[70vh]">
                                {loadingFields ? (
                                    <div className="flex justify-center items-center py-8">
                                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                                        <span className="ml-3 text-gray-600">Loading fields...</span>
                                    </div>
                                ) : mandatoryFieldsByCategory.length === 0 ? (
                                    <div className="text-center py-8 text-gray-500">
                                        No fields found
                                    </div>
                                ) : (
                                    <div className="space-y-6">
                                        {mandatoryFieldsByCategory.map((category, categoryIdx) => {
                                            if (isMultiStep && categoryIdx !== currentStep) {
                                                return null;
                                            }
                                            return (
                                                <div key={categoryIdx} className="space-y-4">
                                                    <h4
                                                        className={clsx(
                                                            "text-sm font-medium border-b pb-2 text-gray-700 dark:text-gray-200 border-gray-200 dark:border-dark-600"
                                                        )}
                                                    >
                                                        {category.categoryName}
                                                    </h4>
                                                    <div className="space-y-3">
                                                        {category.fields.map((field, fieldIdx) => (
                                                            <div
                                                                key={field.Id || fieldIdx}
                                                                className="animate-fadeInUp"
                                                                style={{
                                                                    animationDelay: `${fieldIdx * 80}ms`,
                                                                    animationDuration: "400ms",
                                                                }}
                                                            >
                                                                {renderField(field, fieldIdx, categoryIdx)}
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            )
                                        })}
                                        {customFields.map((category, categoryIdx) => (
                                            <div key={`custom-${categoryIdx}`} className="space-y-4">
                                                <h4 className={clsx(
                                                    "text-sm font-medium border-b pb-2 text-gray-700 dark:text-gray-200 border-gray-200 dark:border-dark-600"
                                                )}>
                                                    {category.categoryName} <span className="text-xs text-primary-600 ml-1"></span>
                                                </h4>
                                                <div className="space-y-3">
                                                    {category.fields.map((field, fieldIdx) => (
                                                        <div
                                                            key={field.Id || fieldIdx}
                                                            className="animate-fadeInUp"
                                                            style={{
                                                                animationDelay: `${fieldIdx * 80}ms`,
                                                                animationDuration: "400ms",
                                                            }}
                                                        >
                                                            {renderField(field, fieldIdx, categoryIdx)}
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {/* FOOTER */}
                            <div className="border-t border-gray-200 px-6 py-4 flex justify-end gap-3 dark:border-dark-600">
                                <Button
                                    variant="default"
                                    onClick={handleClose}
                                    ref={cancelButtonRef}
                                    disabled={isSaving}
                                    className="transition-all duration-200 hover:scale-105"
                                >
                                    Cancel
                                </Button>
                                {isEditMode ? (
                                    <Button
                                        variant="solid"
                                        onClick={handleSave}
                                        disabled={isSaving || loadingFields}
                                        className={clsx(
                                            "bg-primary-600 hover:bg-primary-700 focus:ring-primary-500 text-white transition-all duration-200 hover:scale-105 hover:shadow-lg transform-gpu"
                                        )}
                                    >
                                        {isSaving ? "Updating..." : "Update Lead"}
                                    </Button>
                                ) : (
                                    <>
                                        {isMultiStep && currentStep > 0 && (
                                            <Button
                                                variant="outline"
                                                onClick={() => setCurrentStep(prev => prev - 1)}
                                                className="mr-auto"
                                            >
                                                Back
                                            </Button>
                                        )}

                                        {isMultiStep && currentStep < totalSteps - 1 ? (
                                            <Button
                                                variant="solid"
                                                onClick={async () => {
                                                    // Validate current step fields before moving next
                                                    const currentCategory = mandatoryFieldsByCategory[currentStep];
                                                    let hasError = false;
                                                    const nextErrors = { ...formErrors };

                                                    currentCategory.fields.forEach(field => {
                                                        if (field.IsMendatory && !formValues[field.FieldName]) {
                                                            hasError = true;
                                                            nextErrors[field.FieldName] = "This field is required";
                                                        }
                                                    });

                                                    if (hasError) {
                                                        setFormErrors(nextErrors);
                                                        toast.error("Please fill all mandatory fields");

                                                        // Auto-scroll to first error in current step
                                                        const firstErrorField = Object.keys(nextErrors).find(key =>
                                                            currentCategory.fields.some(f => f.FieldName === key)
                                                        );
                                                        if (firstErrorField && fieldRefs[firstErrorField]) {
                                                            setTimeout(() => {
                                                                fieldRefs[firstErrorField].scrollIntoView({
                                                                    behavior: 'smooth',
                                                                    block: 'center'
                                                                });
                                                            }, 100);
                                                        }
                                                        return;
                                                    }

                                                    setCurrentStep(prev => prev + 1);
                                                }}
                                                className="bg-primary-600 text-white"
                                            >
                                                Next
                                            </Button>
                                        ) : (
                                            <Button
                                                variant="solid"
                                                onClick={handleSave}
                                                disabled={isSaving || loadingFields}
                                                className={clsx(
                                                    "bg-primary-600 hover:bg-primary-700 focus:ring-primary-500 text-white transition-all duration-200 hover:scale-105 hover:shadow-lg transform-gpu"
                                                )}
                                            >
                                                {isSaving ? (
                                                    <>
                                                        <svg
                                                            className="animate-spin -ml-1 mr-2 h-4 w-4 text-white inline"
                                                            xmlns="http://www.w3.org/2000/svg"
                                                            fill="none"
                                                            viewBox="0 0 24 24"
                                                        >
                                                            <circle
                                                                className="opacity-25"
                                                                cx="12"
                                                                cy="12"
                                                                r="10"
                                                                stroke="currentColor"
                                                                strokeWidth="4"
                                                            />
                                                            <path
                                                                className="opacity-75"
                                                                fill="currentColor"
                                                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                                            />
                                                        </svg>
                                                        Saving...
                                                    </>
                                                ) : (
                                                    "Save Lead"
                                                )}
                                            </Button>
                                        )}
                                    </>
                                )}
                            </div>
                        </DialogPanel>
                    </TransitionChild>
                </Dialog>
            </Transition>
        </>
    );
};

export default QuickLeadModal;
